import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { validateCronExpression } from "../../components/CronExpressionBuilder";
import CustomSnackbar from "../../components/CustomSnackbar";
import TaskSchedulerService from "./TaskSchedulerService";
import TaskTabScheduler from "./TaskTabScheduler";
import {
    DEFAULT_TASK_SCHEDULER_CRON,
    TASK_SCHEDULER_TYPES,
    buildTaskSchedulerPayloadForApi,
    getDefaultTaskSchedulerPayload,
    normalizeTaskSchedulerPayload,
} from "./taskSchedulerConstants";
import { ADMIN_TASK_SCHEDULER_LIST_PATH } from "./taskSchedulerRoutes";

export default function TaskSchedulerFormPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);

    const [loading, setLoading] = useState(isEdit);
    const [cron, setCron] = useState(DEFAULT_TASK_SCHEDULER_CRON);
    const [type, setType] = useState(TASK_SCHEDULER_TYPES[0].value);
    const [enabled, setEnabled] = useState(false);
    const [memo, setMemo] = useState("");
    const [payload, setPayload] = useState(() => getDefaultTaskSchedulerPayload(TASK_SCHEDULER_TYPES[0].value));

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState("info");
    const [snackMessage, setSnackMessage] = useState("");
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!isEdit) return undefined;
        let cancelled = false;
        (async () => {
            setLoading(true);
            const result = await TaskSchedulerService.getById(id);
            if (cancelled) return;
            setLoading(false);
            if (result.success && result.data) {
                const s = result.data;
                const taskType = s.type || TASK_SCHEDULER_TYPES[0].value;
                setCron(s.cron || DEFAULT_TASK_SCHEDULER_CRON);
                setType(taskType);
                setEnabled(Boolean(s.enabled));
                setMemo(s.memo ?? "");
                setPayload(normalizeTaskSchedulerPayload(taskType, s.payload));
            } else {
                setSeverity("error");
                setSnackMessage(result.error || "Failed to load schedule");
                setOpenSnackbar(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [id, isEdit]);

    const handleSnack = (sev, msg) => {
        setSeverity(sev);
        setSnackMessage(msg);
        setOpenSnackbar(true);
    };

    const handleTypeChange = (nextType) => {
        setType(nextType);
        setPayload(getDefaultTaskSchedulerPayload(nextType));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const validation = validateCronExpression(cron);
        if (!validation.valid) {
            handleSnack("error", validation.error);
            return;
        }
        const trimmedMemo = memo.trim();
        const body = {
            cron: cron.trim(),
            type,
            enabled,
            memo: trimmedMemo === "" ? (isEdit ? null : undefined) : trimmedMemo,
            payload: buildTaskSchedulerPayloadForApi(type, payload),
        };
        if (!isEdit && trimmedMemo === "") {
            delete body.memo;
        }
        (async () => {
            const result = isEdit
                ? await TaskSchedulerService.update(id, body)
                : await TaskSchedulerService.create(body);
            if (result.success) {
                navigate(ADMIN_TASK_SCHEDULER_LIST_PATH, {
                    state: { message: isEdit ? "Schedule updated" : "Schedule created" },
                });
            } else {
                handleSnack("error", result.error || "Save failed");
            }
        })();
    };

    const handleDelete = async () => {
        setOpenDeleteDialog(false);
        setDeleting(true);
        const result = await TaskSchedulerService.delete(id);
        setDeleting(false);
        if (result.success) {
            navigate(ADMIN_TASK_SCHEDULER_LIST_PATH, { state: { message: "Schedule deleted" } });
        } else {
            handleSnack("error", result.error || "Delete failed");
        }
    };

    if (loading) {
        return (
            <Box sx={{ flexGrow: 1, p: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton aria-label="Back" onClick={() => navigate(ADMIN_TASK_SCHEDULER_LIST_PATH)} size="small">
                    <ArrowBackIcon />
                </IconButton>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                <IconButton
                    aria-label="Back to list"
                    onClick={() => navigate(ADMIN_TASK_SCHEDULER_LIST_PATH)}
                    size="small"
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" component="h1" sx={{ mb: 0 }}>
                    {isEdit ? "Edit task schedule" : "New task schedule"}
                </Typography>
            </Box>

            <TaskTabScheduler
                cron={cron}
                onCronChange={setCron}
                type={type}
                onTypeChange={handleTypeChange}
                enabled={enabled}
                onEnabledChange={setEnabled}
                memo={memo}
                onMemoChange={setMemo}
                payload={payload}
                onPayloadChange={setPayload}
                onSubmit={handleSubmit}
                onCancel={() => navigate(ADMIN_TASK_SCHEDULER_LIST_PATH)}
                isEdit={isEdit}
                onDelete={isEdit ? () => setOpenDeleteDialog(true) : undefined}
            />

            <Dialog open={openDeleteDialog} onClose={() => !deleting && setOpenDeleteDialog(false)}>
                <DialogTitle>Delete schedule?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This removes the row and reloads Quartz on the server. This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
                        {deleting ? "Deleting…" : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            <CustomSnackbar
                open={openSnackbar}
                setOpen={setOpenSnackbar}
                severity={severity}
                message={snackMessage}
            />
        </Box>
    );
}
